/* ---------------------------------------------------------------------------
   What to print where nobody has recorded anything.

   This exists because of one change and is worth the file. When the register
   served one department whose five records came off as-built drawings, `spans`,
   `year`, `type` and the rest were required and every screen could format them
   unconditionally. Serving a department whose source list carries six facts per
   structure made them nullable, and that turned a hundred-odd call sites into a
   hundred-odd small decisions about how to say *not recorded*.

   Made once, here, they are consistent. Made at each site they would be `—` in
   one place, `n/a` in another, `0` in the two that forgot, and `undefined m` in
   the one that concatenated before checking. The last of those is the reason this
   is not a matter of taste: a zero and an empty string read as measurements. A
   deck 0.0 m wide is a fact about a bridge, and it is not true of any bridge.

   THE DASH IS AN EM DASH, U+2014, and it is the same dash the rest of the
   interface already used for an absent inspection date and an unscreened hazard
   band. One mark for "there is nothing here" across every screen, so a reader
   learns it once.

   NOTHING HERE ROUNDS OR GUESSES. `metres(null)` is a dash; `metres(0)` is
   `0.00 m`, because a recorded zero is a record and this module's job is to
   distinguish the two, not to blur them.
   --------------------------------------------------------------------------- */

/** The one mark for a value nobody has recorded. */
export const NOT_RECORDED = '—';

/**
 * The prose form, for a help line or a tooltip rather than a table cell.
 *
 * A dash in a sentence reads as a punctuation mistake, and a tooltip that says
 * "Deck width —" tells a reader less than nothing.
 */
export const NOT_RECORDED_TEXT = 'Not recorded';

/** True where a value is absent or a number that is not one. */
export function isRecorded(v: string | number | null | undefined): boolean {
  if (v == null) return false;
  if (typeof v === 'number') return Number.isFinite(v);
  return v.trim() !== '';
}

/** A recorded value as text, or the dash. The general case. */
export function shown(v: string | number | null | undefined): string {
  return isRecorded(v) ? String(v).trim() : NOT_RECORDED;
}

/**
 * A number with its unit, or the dash — never the unit on its own.
 *
 * `metres(b.deckWidth)` rather than `` `${b.deckWidth} m` ``, which on an
 * unrecorded width printed `null m`. The unit goes inside so it cannot be left
 * stranded beside a dash.
 */
export function withUnit(
  v: number | null | undefined,
  unit: string,
  digits = 2,
): string {
  return v != null && Number.isFinite(v) ? `${v.toFixed(digits)} ${unit}` : NOT_RECORDED;
}

/** Metres to two places, or the dash. The unit this register mostly speaks. */
export function metres(v: number | null | undefined, digits = 2): string {
  return withUnit(v, 'm', digits);
}

/** A whole count — spans, lanes, years — or the dash. */
export function count(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) ? String(v) : NOT_RECORDED;
}

/**
 * Two values joined, dropping the ones nobody recorded.
 *
 * `join([b.district, b.province])` gives "Chitwan, Bagmati" for a record with
 * both, "Samar 2nd District" for one with only the first, and the dash for
 * neither — rather than "Samar 2nd District, " with a comma hanging off it.
 */
export function join(parts: readonly (string | number | null | undefined)[], sep = ', '): string {
  const kept = parts.filter(isRecorded).map((p) => String(p).trim());
  return kept.length ? kept.join(sep) : NOT_RECORDED;
}

/**
 * A phrase built from parts, or null if nothing in it was recorded.
 *
 * The difference from `join` is what it returns when empty: null, so a caller
 * can omit a whole row rather than print a row containing a dash. An inventory
 * panel with eight dashes in it is a panel that has stopped being read.
 */
export function phrase(parts: readonly (string | number | null | undefined)[], sep = ' · '): string | null {
  const kept = parts.filter(isRecorded).map((p) => String(p).trim());
  return kept.length ? kept.join(sep) : null;
}

/**
 * A total over a column not every record has, and how many it skipped.
 *
 * BOTH NUMBERS, ALWAYS. A sum that quietly ignores the records with no figure is
 * a sum that understates by exactly the amount nobody knows, and the whole
 * difference between that being a bug and being a fact is whether the caller was
 * told. `missing` is what lets a screen print "3 380 m over 107 of 108
 * structures" instead of a total that looks complete and is not.
 *
 * Callers that genuinely only need the number take `.total` — which is honest as
 * long as they say so somewhere, and every one of them in this application does.
 */
export function sumRecorded<T>(
  list: readonly T[],
  pick: (item: T) => number | null | undefined,
): { total: number; missing: number } {
  let total = 0;
  let missing = 0;
  for (const item of list) {
    const v = pick(item);
    if (v != null && Number.isFinite(v)) total += v;
    else missing += 1;
  }
  return { total, missing };
}

/**
 * The largest recorded value in a list, by a number read off each item.
 *
 * Null where nothing in the list has one — which is not the same as an empty
 * list and is treated the same way on purpose: "no longest structure" is the
 * answer to both. A plain `reduce` with `>` gets this wrong in a way that is
 * hard to see, because `null > 5` is false and `5 > null` is true, so whether an
 * unrecorded value wins depends on where it sits in the array.
 */
export function maxRecorded<T>(
  list: readonly T[],
  pick: (item: T) => number | null | undefined,
): T | null {
  let best: T | null = null;
  let bestValue = -Infinity;
  for (const item of list) {
    const v = pick(item);
    if (v == null || !Number.isFinite(v)) continue;
    if (v > bestValue) {
      best = item;
      bestValue = v;
    }
  }
  return best;
}

/** Distinct recorded values of one text column, in the order first seen. */
export function distinct<T>(
  list: readonly T[],
  pick: (item: T) => string | null | undefined,
): string[] {
  const out: string[] = [];
  for (const item of list) {
    const v = pick(item);
    if (v != null && v.trim() !== '' && !out.includes(v)) out.push(v);
  }
  return out;
}
